import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {mouse} from '@common/mouse'
import {IconButton} from '@components/common/base/buttons'
import {usePopstateListener} from '@components/common/popup/use-popstate-listener'
import {crmStore} from '@components/crm/crm-store'
import {MoveToBody} from '@components/move-to-body'
import {noPropagate, useBoard, useGlobalKeyDown} from '@containers/hooks'
import {Actions, MultiActionVisibleItem} from './actions/actions'
import {
  MultiActionsWithTasksContext,
  multiActionsWithTasksStore,
  useMultiActionsWithTasksStore
} from './multi-actions-with-tasks-store'

export const MultiActionsWithTasks = () => {
  return (
    <MultiActionsWithTasksContext.Provider value={multiActionsWithTasksStore}>
      <MultiActionsWithTasksBase />
    </MultiActionsWithTasksContext.Provider>
  )
}

const MultiActionsWithTasksBase = observer(() => {
  const [state, store] = useMultiActionsWithTasksStore()
  const board = useBoard()

  useEffect(() => {
    if (board && state.visible) {
      store.setColumnTasks(board)
    }
  }, [board, state.visible])

  useEffect(() => {
    state.visible && store.setSelectedColumnTasks()
  }, [state.visible, state.tasksIds.size])

  const close = () => store.visibleOff()
  const globalClose = () => {
    const visiblePopup =
      state.deletePopup ||
      state.noRightsToSomeTasks ||
      state.applyCustomStickerPopup
    return !visiblePopup && !mouse.dragItem && close()
  }

  useGlobalKeyDown({
    escape: globalClose
  })
  usePopstateListener(globalClose)

  if (!state.visible) {
    return null
  }

  const taskCount = state.tasksIds.size
  const archivedTaskCount = store.archivedTasksSize

  let visibleActions: MultiActionVisibleItem[]

  if (crmStore.isCrmBoard(board)) {
    visibleActions = ['delete', 'more']
  }

  return (
    <TwMoveToBody zIndex={2001} className="pointer-events-none">
      <TwWrapper
        $hidden={
          state.deletePopup ||
          state.noRightsToSomeTasks ||
          state.applyCustomStickerPopup
        }
        onClick={noPropagate}
      >
        <TwControls className="pointer-events-auto">
          <TwTextWrapper>
            <TwText>
              {Lang.msg.multiActionsWithTasks.allocatedTasks(taskCount)}
            </TwText>
            {archivedTaskCount > 0 && (
              <TwTextArchived>
                {Lang.msg.multiActionsWithTasks.fromArchive(archivedTaskCount)}
              </TwTextArchived>
            )}
          </TwTextWrapper>
          <Actions visibleItems={visibleActions} />
          <IconButton
            icon="IconX"
            color="secondary"
            size="small"
            wrapperSize="big"
            className="-m-4"
            onClick={close}
          />
        </TwControls>
      </TwWrapper>
    </TwMoveToBody>
  )
})

const TwControls = tw.div`
  bg-background-primary-92
  rounded-8
  py-12
  px-16
  flex
  items-center
  gap-16
`

const TwMoveToBody = tw(MoveToBody)`
  bottom-18
  absolute
  w-full
`

const TwText = tw.div`
  text-primary
`

const TwTextArchived = tw.div`
  text-secondary
  before:content-["("]
  after:content-[")"]
`

const TwWrapper = tw.div<{$hidden: boolean}>`
  ${p => (p.$hidden ? 'invisible' : '')}
  flex
  justify-center
`

const TwTextWrapper = tw.div`
  select-none
  flex
  items-center
  gap-4
  text-sm-regular
`
